import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { partnerDiscountService } from '../services/api';
import { PartnerOrganizationAdmin } from '../types';

const PartnerDiscountsManagement: React.FC = () => {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<PartnerOrganizationAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [orgForm, setOrgForm] = useState({
    name: '',
    slug: '',
    verification_type: 'email_otp',
    email_domain: '',
    validation_url: '',
    validation_token: '',
    is_active: true,
  });

  const [campaignForm, setCampaignForm] = useState({
    org_id: '',
    discount_type: 'amount',
    discount_value: '',
    currency: 'USD',
    description: '',
    starts_at: '',
    ends_at: '',
    is_active: true,
  });
  const [editingCampaign, setEditingCampaign] = useState<{
    orgId: number;
    id: number;
    discount_type: 'amount' | 'percent';
    discount_value: string;
    currency: string;
    description: string;
    starts_at: string;
    ends_at: string;
    is_active: boolean;
  } | null>(null);

  const [rosterForm, setRosterForm] = useState({
    org_id: '',
    rows: '',
  });

  useEffect(() => {
    if (!user?.is_admin) return;
    const load = async () => {
      try {
        setLoading(true);
        const list = await partnerDiscountService.listOrgs();
        setOrgs(list.map((org) => ({ ...org, campaigns: org.campaigns ?? [] })));
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to load partner orgs.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleCreateOrg = async (event: React.FormEvent) => {
    event.preventDefault();
    resetMessages();
    try {
      const payload = {
        ...orgForm,
        slug: orgForm.slug.trim().toLowerCase(),
        email_domain: orgForm.email_domain.trim() || null,
        validation_url: orgForm.validation_url.trim() || null,
        validation_token: orgForm.validation_token.trim() || null,
      };
      const response = await partnerDiscountService.createOrg(payload);
      const nextOrg = { ...response.org, campaigns: response.org.campaigns ?? [] };
      setOrgs((prev) => [nextOrg, ...prev]);
      setOrgForm({
        name: '',
        slug: '',
        verification_type: 'email_otp',
        email_domain: '',
        validation_url: '',
        validation_token: '',
        is_active: true,
      });
      setSuccess('Partner organization created.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to create organization.');
    }
  };

  const handleCreateCampaign = async (event: React.FormEvent) => {
    event.preventDefault();
    resetMessages();
    try {
      const orgId = Number(campaignForm.org_id);
      if (!orgId) {
        setError('Select an organization for the campaign.');
        return;
      }
      const response = await partnerDiscountService.createCampaign(orgId, {
        discount_type: campaignForm.discount_type as 'amount' | 'percent',
        discount_value: Number(campaignForm.discount_value),
        currency: campaignForm.currency.toUpperCase(),
        description: campaignForm.description || null,
        starts_at: campaignForm.starts_at || null,
        ends_at: campaignForm.ends_at || null,
        is_active: campaignForm.is_active,
      });
      const newCampaign = response.campaign;
      setOrgs((prev) => prev.map((org) => (
        org.id === orgId ? { ...org, campaigns: [newCampaign, ...org.campaigns] } : org
      )));
      setCampaignForm({ org_id: '', discount_type: 'amount', discount_value: '', currency: 'USD', description: '', starts_at: '', ends_at: '', is_active: true });
      setSuccess('Campaign created.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to create campaign.');
    }
  };

  const parseRosterRows = () => {
    const rows = rosterForm.rows
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    return rows.map((line) => {
      const [email, member_id, last_name] = line.split(',').map((value) => value?.trim());
      return { email, member_id, last_name };
    }).filter((entry) => entry.email || entry.member_id);
  };

  const handleUploadRoster = async (event: React.FormEvent) => {
    event.preventDefault();
    resetMessages();
    try {
      const orgId = Number(rosterForm.org_id);
      if (!orgId) {
        setError('Select an organization for the roster.');
        return;
      }
      const entries = parseRosterRows();
      if (entries.length === 0) {
        setError('Add at least one roster row.');
        return;
      }
      const response = await partnerDiscountService.uploadRoster(orgId, entries);
      setRosterForm({ org_id: '', rows: '' });
      setSuccess(`Roster saved: ${response.created} new, ${response.updated} updated.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to upload roster.');
    }
  };

  const toggleOrgStatus = async (org: PartnerOrganizationAdmin) => {
    resetMessages();
    try {
      const response = await partnerDiscountService.updateOrg(org.id, { is_active: !org.is_active });
      setOrgs((prev) => prev.map((item) => (item.id === org.id ? { ...item, ...response.org } : item)));
      setSuccess(`Organization ${org.is_active ? 'deactivated' : 'reactivated'}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update organization.');
    }
  };

  const toggleCampaignStatus = async (orgId: number, campaignId: number, isActive: boolean) => {
    resetMessages();
    try {
      const response = await partnerDiscountService.updateCampaign(orgId, campaignId, { is_active: !isActive });
      const updatedCampaign = response.campaign;
      setOrgs((prev) => prev.map((org) => (
        org.id === orgId
          ? { ...org, campaigns: org.campaigns.map((camp) => (camp.id === campaignId ? updatedCampaign : camp)) }
          : org
      )));
      setSuccess(`Campaign ${isActive ? 'deactivated' : 'reactivated'}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update campaign.');
    }
  };

  const startEditingCampaign = (orgId: number, campaign: any) => {
    setEditingCampaign({
      orgId,
      id: campaign.id,
      discount_type: campaign.discount_type || 'amount',
      discount_value: campaign.discount_value != null ? String(campaign.discount_value) : '',
      currency: campaign.currency || 'USD',
      description: campaign.description || '',
      starts_at: campaign.starts_at || '',
      ends_at: campaign.ends_at || '',
      is_active: Boolean(campaign.is_active),
    });
  };

  const handleUpdateCampaign = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingCampaign) return;
    resetMessages();
    try {
      const payload = {
        discount_type: editingCampaign.discount_type,
        discount_value: Number(editingCampaign.discount_value),
        currency: editingCampaign.currency.toUpperCase(),
        description: editingCampaign.description || null,
        starts_at: editingCampaign.starts_at || null,
        ends_at: editingCampaign.ends_at || null,
        is_active: editingCampaign.is_active,
      };
      const response = await partnerDiscountService.updateCampaign(editingCampaign.orgId, editingCampaign.id, payload);
      const updatedCampaign = response.campaign;
      setOrgs((prev) => prev.map((org) => (
        org.id === editingCampaign.orgId
          ? { ...org, campaigns: org.campaigns.map((camp) => (camp.id === editingCampaign.id ? updatedCampaign : camp)) }
          : org
      )));
      setEditingCampaign(null);
      setSuccess('Campaign updated.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update campaign.');
    }
  };

  const orgOptions = useMemo(() => orgs.map((org) => ({ id: org.id, name: org.name })), [orgs]);

  if (!user?.is_admin) {
    return <div className="text-center py-20 text-red-400">Admin access required.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Discount Organizations</h1>
          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Manage discount organizations, campaigns, and eligibility rosters.</p>
        </div>
      </div>

      {(error || success) && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {error || success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={handleCreateOrg} className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Create discount organization</h2>
          </div>
          <div className="p-6 space-y-4">
            <input
              type="text"
              required
              placeholder="Organization name"
              value={orgForm.name}
              onChange={(event) => setOrgForm((prev) => ({ ...prev, name: event.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <input
              type="text"
              required
              placeholder="Slug (e.g. msa-university)"
              value={orgForm.slug}
              onChange={(event) => setOrgForm((prev) => ({ ...prev, slug: event.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select
                value={orgForm.verification_type}
                onChange={(event) => setOrgForm((prev) => ({ ...prev, verification_type: event.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="email_otp">Email OTP</option>
                <option value="member_id">Member ID</option>
                <option value="api">API Validation</option>
              </select>
              <input
                type="text"
                placeholder="Email domain (optional)"
                value={orgForm.email_domain}
                onChange={(event) => setOrgForm((prev) => ({ ...prev, email_domain: event.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            {orgForm.verification_type === 'api' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Validation URL"
                  value={orgForm.validation_url}
                  onChange={(event) => setOrgForm((prev) => ({ ...prev, validation_url: event.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Validation token (optional)"
                  value={orgForm.validation_token}
                  onChange={(event) => setOrgForm((prev) => ({ ...prev, validation_token: event.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={orgForm.is_active}
                onChange={(event) => setOrgForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                className="rounded border-gray-300"
              />
              Active
            </label>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-5 py-2 text-sm"
            >
              Create organization
            </button>
          </div>
        </form>

        <form onSubmit={handleCreateCampaign} className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Create campaign</h2>
          </div>
          <div className="p-6 space-y-4">
            <select
              required
              value={campaignForm.org_id}
              onChange={(event) => setCampaignForm((prev) => ({ ...prev, org_id: event.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">Select organization</option>
              {orgOptions.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select
                value={campaignForm.discount_type}
                onChange={(event) => setCampaignForm((prev) => ({ ...prev, discount_type: event.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="amount">Amount</option>
                <option value="percent">Percent</option>
              </select>
              <input
                type="number"
                required
                step="0.01"
                placeholder={campaignForm.discount_type === 'percent' ? 'Percent (e.g. 10)' : 'Discount amount'}
                value={campaignForm.discount_value}
                onChange={(event) => setCampaignForm((prev) => ({ ...prev, discount_value: event.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            {campaignForm.discount_type === 'amount' && (
              <input
                type="text"
                required
                placeholder="Currency (USD)"
                value={campaignForm.currency}
                onChange={(event) => setCampaignForm((prev) => ({ ...prev, currency: event.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            )}
            <textarea
              placeholder="Description (optional)"
              value={campaignForm.description}
              onChange={(event) => setCampaignForm((prev) => ({ ...prev, description: event.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              rows={3}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="date"
                value={campaignForm.starts_at}
                onChange={(event) => setCampaignForm((prev) => ({ ...prev, starts_at: event.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
              <input
                type="date"
                value={campaignForm.ends_at}
                onChange={(event) => setCampaignForm((prev) => ({ ...prev, ends_at: event.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={campaignForm.is_active}
                onChange={(event) => setCampaignForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                className="rounded border-gray-300"
              />
              Active
            </label>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-5 py-2 text-sm"
            >
              Create campaign
            </button>
          </div>
        </form>

        <form onSubmit={handleUploadRoster} className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden lg:col-span-2">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Upload roster</h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-500">Paste CSV rows in the format: email,member_id,last_name</p>
            <div className="mt-4 space-y-4">
            <select
              required
              value={rosterForm.org_id}
              onChange={(event) => setRosterForm((prev) => ({ ...prev, org_id: event.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">Select organization</option>
              {orgOptions.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            <textarea
              value={rosterForm.rows}
              onChange={(event) => setRosterForm((prev) => ({ ...prev, rows: event.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              rows={6}
              placeholder="student@school.edu,12345,smith"
            />
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-5 py-2 text-sm"
              >
                Upload roster
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Current organizations</h2>
        </div>
        <div className="p-6">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="space-y-4">
              {orgs.map((org) => (
                <div key={org.id} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{org.name}</p>
                    <p className="text-xs text-gray-500">Slug: {org.slug} · {org.verification_type}</p>
                    <p className="text-xs text-gray-500">
                      Campaign URL: {`${window.location.origin}/partner/${org.slug}`}
                    </p>
                    {org.email_domain && <p className="text-xs text-gray-500">Domain: {org.email_domain}</p>}
                    {org.validation_url && <p className="text-xs text-gray-500">Validation URL: {org.validation_url}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${org.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                      {org.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleOrgStatus(org)}
                      className="text-xs font-semibold text-gray-700 hover:text-gray-900"
                    >
                      {org.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-widest text-gray-500">Campaigns</p>
                  {org.campaigns.length === 0 ? (
                    <p className="text-sm text-gray-500">No campaigns yet.</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                  {org.campaigns.map((campaign) => (
                        <div key={campaign.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                          <div className="flex items-center justify-between">
                            <span>
                              {campaign.discount_type === 'percent'
                                ? `${campaign.discount_value}%`
                                : `${campaign.currency} ${campaign.discount_value.toFixed(2)}`}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs ${campaign.is_active ? 'text-emerald-600' : 'text-gray-400'}`}>
                                {campaign.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleCampaignStatus(org.id, campaign.id, campaign.is_active)}
                                className="text-xs font-semibold text-gray-700 hover:text-gray-900"
                              >
                                {campaign.is_active ? 'Deactivate' : 'Reactivate'}
                              </button>
                              <button
                                type="button"
                                onClick={() => startEditingCampaign(org.id, campaign)}
                                className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                          {campaign.description && <p className="text-xs text-gray-500 mt-1">{campaign.description}</p>}
                          {editingCampaign?.id === campaign.id && editingCampaign.orgId === org.id && (
                            <form onSubmit={handleUpdateCampaign} className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/60 p-3 space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <select
                                  value={editingCampaign.discount_type}
                                  onChange={(event) => setEditingCampaign((prev) => prev ? { ...prev, discount_type: event.target.value as 'amount' | 'percent' } : prev)}
                                  className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                >
                                  <option value="amount">Amount</option>
                                  <option value="percent">Percent</option>
                                </select>
                                <input
                                  type="number"
                                  required
                                  placeholder="Discount value"
                                  value={editingCampaign.discount_value}
                                  onChange={(event) => setEditingCampaign((prev) => prev ? { ...prev, discount_value: event.target.value } : prev)}
                                  className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                  type="text"
                                  placeholder="Currency (USD)"
                                  value={editingCampaign.currency}
                                  onChange={(event) => setEditingCampaign((prev) => prev ? { ...prev, currency: event.target.value } : prev)}
                                  className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                />
                                <input
                                  type="text"
                                  placeholder="Description"
                                  value={editingCampaign.description}
                                  onChange={(event) => setEditingCampaign((prev) => prev ? { ...prev, description: event.target.value } : prev)}
                                  className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                  type="date"
                                  value={editingCampaign.starts_at}
                                  onChange={(event) => setEditingCampaign((prev) => prev ? { ...prev, starts_at: event.target.value } : prev)}
                                  className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                />
                                <input
                                  type="date"
                                  value={editingCampaign.ends_at}
                                  onChange={(event) => setEditingCampaign((prev) => prev ? { ...prev, ends_at: event.target.value } : prev)}
                                  className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                />
                              </div>
                              <label className="flex items-center gap-2 text-xs text-emerald-700">
                                <input
                                  type="checkbox"
                                  checked={editingCampaign.is_active}
                                  onChange={(event) => setEditingCampaign((prev) => prev ? { ...prev, is_active: event.target.checked } : prev)}
                                  className="rounded border-emerald-300"
                                />
                                Active
                              </label>
                              <div className="flex items-center gap-2">
                                <button type="submit" className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-3 py-1.5 text-xs">
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingCampaign(null)}
                                  className="text-xs font-semibold text-emerald-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerDiscountsManagement;
